// @ts-nocheck
import React from 'react';
import flatMap from 'lodash/flatMap';

import NotFound404 from './components/Common/NotFound404';
import { getLabelPathFromLabelConfig, reactLazyRetry } from './utils/Utils';
import SuspenseComponent from './components/Common/SuspenseComponent';
import ErrorBoundary from './components/Common/ErrorBoundary';
import ClearCacheLogout from './components/ClearCacheLogout/ClearCacheLogout';
import PluginAccessView from './plugins/PluginAccessView';

import { homeRoutes } from './features/home/home.routes';
import { bulkActionsRoutes } from './features/bulkActions/bulkActions.routes';
import { configurationRoutes } from './features/configuration/configuration.routes';
import { reportAndDashboardRoutes } from './features/reportAndDashboard/reportAndDashboard.routes';
import { oldReportsRoutes } from './features/oldReports/oldReports.routes';
import { administrativeOptionsRoutes } from './features/administrativeOptions/administrativeOptions.routes';
import { documentRoutes } from './features/document/document.routes';
import { logsRoutes } from './features/logs/logs.routes';
import { buildInventoryFeatureRoutes } from './features/inventory/inventory.routes';

const Layout = reactLazyRetry(() => import('./features/Layout/Layout'));
const Login = reactLazyRetry(() => import('./features/Login/Login'));
const Account = reactLazyRetry(() => import('./features/Login/Account'));
const Organization = reactLazyRetry(
  () => import('./features/Login/Organization')
);

const HelpAndSupport = reactLazyRetry(
  () => import('./features/configuration/helpAndSupport/pages/HelpAndSupport')
);
const NotAuthorized = reactLazyRetry(
  () => import('./components/Common/NotAuthorized')
);
const HandleRedirectionLink = reactLazyRetry(
  () =>
    import('./components/Common/HandleRedirectionLink/HandleRedirectionLink')
);

export const Routes = [
  {
    name: ' NotAuthorized',
    key: 'notAuthorized',
    path: 'not-authorized',
    element: <NotAuthorized />,
  },
  {
    name: 'Handle Redirection Link',
    key: 'handleRedirectionLink',
    path: 'handle-redirection-link',
    element: <HandleRedirectionLink />,
  },
  ...homeRoutes,
  // inventoryRoutes, (Added exceptionally below as "buildInventoryFeatureRoutes")
  ...bulkActionsRoutes,
  ...configurationRoutes,
  ...oldReportsRoutes,
  ...reportAndDashboardRoutes,
  ...administrativeOptionsRoutes,
  ...documentRoutes,
  ...logsRoutes,
  // + Add new routes here
];

/**
 * @type {import('react-router-dom').RouteObject[]}
 * */
export const publicRoutes = [
  {
    name: 'Login',
    path: '/login',
    element: <Login />,
    errorElement: <ErrorBoundary />,
    quickLink: { enabled: false },
  },
  {
    name: 'Login With Account',
    path: '/login/:accountId',
    element: <Login />,
    errorElement: <ErrorBoundary />,
    quickLink: { enabled: false },
  },
  {
    name: 'Account',
    path: '/account',
    element: <Account />,
    errorElement: <ErrorBoundary />,
    quickLink: { enabled: false },
  },
  {
    name: 'Organization',
    path: '/org',
    element: <Organization />,
    errorElement: <ErrorBoundary />,
    quickLink: { enabled: false },
  },
  {
    name: 'Clear Client Storage',
    path: '/clear-client-storage',
    element: <ClearCacheLogout />,
    errorElement: <ErrorBoundary />,
    quickLink: { enabled: false },
  },

  {
    name: 'Not Found',
    path: '*',
    element: <NotFound404 />,
    errorElement: <ErrorBoundary />,
    quickLink: { enabled: false },
  },
];

/**
 * @type {import('react-router-dom').RouteObject[]}
 */
export const pluginRoutes = [
  {
    name: 'UAM Requests',
    path: '/uam-requests',
    element: (
      <PluginAccessView
        pluginKey="HDFCUAM.views.UAMRequestListView"
        permissions={['HDFCUAM_UAMMaker', 'HDFCUAM_UAMChecker']}
      />
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    name: 'UAM Request Approval',
    path: '/uam-requests/:uniqueId',
    element: (
      <PluginAccessView
        pluginKey="HDFCUAM.views.UAMRequestApprovalView"
        permissions={['HDFCUAM_UAMMaker', 'HDFCUAM_UAMChecker']}
      />
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    name: 'UAM Reports',
    path: '/uam-reports',
    element: (
      <PluginAccessView pluginKey="HDFCUAM.views.UAMReportsDownloadView" />
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    name: 'Role Requests',
    path: '/role-requests',
    element: (
      <PluginAccessView
        pluginKey="HDFCUAM.views.RoleRequestsListView"
        permissions={['HDFCUAM_RoleMaker', 'HDFCUAM_RoleChecker']}
      />
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    name: 'Create Role Request',
    path: '/role-requests/new',
    element: (
      <PluginAccessView
        pluginKey="HDFCUAM.views.RoleRequestCreateView"
        permissions={['HDFCUAM_RoleMaker']}
      />
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    name: 'Role Request Approval',
    path: '/role-requests/:requestId',
    element: (
      <PluginAccessView
        pluginKey="HDFCUAM.views.RoleRequestApproverView"
        permissions={['HDFCUAM_RoleMaker', 'HDFCUAM_RoleChecker']}
      />
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    name: 'UAM Parameters',
    path: '/uam-parameters',
    element: (
      <PluginAccessView
        pluginKey="HDFCUAM.views.UAMParametersView"
        permissions={['HDFCUAM_RoleMaker', 'HDFCUAM_RoleChecker']}
      />
    ),
    errorElement: <ErrorBoundary />,
  },
];

const mapRoutes = (routes, userPermissions, isNoGroupUser, parentKey = '') =>
  routes.map(({ path, element, permission, key, children }) => {
    const fullKey = parentKey ? `${parentKey}.${key}` : `MENUS.${key}`;
    const routePath = getLabelPathFromLabelConfig(fullKey, 'path', path);

    const hasNoAccess =
      isNoGroupUser || userPermissions?.[permission] === 'None';
    const mappedRoute = {
      path: routePath || path,
      element: hasNoAccess ? (
        <NotAuthorized />
      ) : (
        <SuspenseComponent>{element}</SuspenseComponent>
      ),
      errorElement: <ErrorBoundary />,
    };
    if (children?.length) {
      mappedRoute.children = mapRoutes(
        children,
        userPermissions,
        isNoGroupUser,
        fullKey
      );
    }

    return mappedRoute;
  });

const flattenRoutes = (routes) =>
  flatMap(routes, (route) => {
    const { children, ...rest } = route;
    return children ? [rest, ...flattenRoutes(children)] : rest;
  });

/**
 * @param {Record<string, 'None' | 'View' | 'Manage'>} userPermissions User Permissions returned from API
 * @returns {import('react-router-dom').RouteObject[]}
 */
export const getProtectedRoutes = (userPermissions = {}, entityTypes = []) => {
  /**
   * if user is not present in any group then he will receive only superadmin bool,
   * i.e object with length 1, logic will break if BE response change
   */
  const isNoGroupUser = Object.keys(userPermissions).length === 1;
  return [
    {
      path: '/',
      element: <Layout />,
      children: [
        ...flattenRoutes(mapRoutes(Routes, userPermissions, isNoGroupUser)),
        ...buildInventoryFeatureRoutes(entityTypes, userPermissions),
        ...pluginRoutes,
      ],
    },
    {
      path: '/help',
      element: (
        <SuspenseComponent>
          <HelpAndSupport />
        </SuspenseComponent>
      ),
      errorElement: <ErrorBoundary />,
    },
  ];
};
